import React, { useEffect } from 'react'
import { FormProvider, useForm, useFormState } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { z } from 'zod'

import { Box, Button, Flex, Heading, VStack } from '@chakra-ui/react'

import { Input } from '@/components/Form'
import { LoadingSection } from '@/components/Elements'
import { Country, COUNTRY_LABELS } from '@/constants/country.constant'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import useConfig from '../useConfig'
import { ConfigCategory, ConfigType } from '../config.enum'

const ADDRESS_LINE_KEYS = ['line1', 'line2', 'line3', 'line4']

const CONFIG_TYPE_BY_REGION = {
  addressHk: ConfigType.SYSTEM_ADDRESS_HK,
  addressMo: ConfigType.SYSTEM_ADDRESS_MO,
}

const addressSchema = z.object({
  line1: z.string().trim().min(1, 'Address Line 1 is required').max(200, 'Max 200 characters'),
  line2: z.string().max(200, 'Max 200 characters').optional(),
  line3: z.string().max(200, 'Max 200 characters').optional(),
  line4: z.string().max(200, 'Max 200 characters').optional(),
})

const AddressFields = ({ region, countryLabel }) => (
  <Box>
    <Heading size="sm" mb={2}>
      {countryLabel}
    </Heading>
    <VStack align="stretch">
      {ADDRESS_LINE_KEYS.map((lineKey, index) => (
        <Input
          key={lineKey}
          name={`${region}.${lineKey}`}
          label={`Address Line ${index + 1}`}
          isRow
          isRequired={lineKey === 'line1'}
        />
      ))}
    </VStack>
  </Box>
)

const SystemConfigPage = () => {
  const dispatch = useDispatch()
  const methods = useForm({
    defaultValues: {
      addressHk: { line1: '', line2: '', line3: '', line4: '' },
      addressMo: { line1: '', line2: '', line3: '', line4: '' },
    },
  })
  const { dirtyFields } = useFormState({ control: methods.control })

  const { useFetchAllConfigByCategory, useBatchUpdateConfigs } = useConfig()
  const { data: systemConfigs, isLoading } = useFetchAllConfigByCategory(ConfigCategory.SYSTEM_CONFIG)
  const batchUpdateConfigs = useBatchUpdateConfigs()

  useEffect(() => {
    if (!Array.isArray(systemConfigs) || systemConfigs.length === 0) {
      return
    }

    systemConfigs.forEach((config) => {
      const region = Object.keys(CONFIG_TYPE_BY_REGION).find((key) => CONFIG_TYPE_BY_REGION[key] === config.type)
      if (!region) {
        return
      }
      ADDRESS_LINE_KEYS.forEach((lineKey, index) => {
        methods.setValue(`${region}.${lineKey}`, config.value?.[index] ?? '')
      })
    })
  }, [systemConfigs, methods])

  const onSubmit = async (data) => {
    const dirtyRegions = Object.keys(CONFIG_TYPE_BY_REGION).filter((region) => dirtyFields[region])

    if (dirtyRegions.length === 0) {
      return
    }

    let hasError = false
    dirtyRegions.forEach((region) => {
      methods.clearErrors(region)
      const result = addressSchema.safeParse(data[region])
      if (!result.success) {
        hasError = true
        result.error.issues.forEach((issue) => {
          methods.setError(`${region}.${issue.path[0]}`, { message: issue.message })
        })
      }
    })

    if (hasError) {
      return
    }

    const updatedConfigs = dirtyRegions.map((region) => ({
      type: CONFIG_TYPE_BY_REGION[region],
      value: ADDRESS_LINE_KEYS.map((lineKey) => data[region][lineKey] ?? ''),
    }))

    try {
      dispatch(showLoadingOverlay())
      await batchUpdateConfigs.mutateAsync({ data: updatedConfigs })
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <Flex p={5} borderBottom="1px solid #d8dbe0">
        <Heading>System Config</Heading>
      </Flex>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box p={5}>
            <Heading size="md" mb={4}>
              Address Management
            </Heading>
            <Flex direction="column" gap="8">
              <AddressFields region="addressHk" countryLabel={COUNTRY_LABELS[Country.HK]} />
              <AddressFields region="addressMo" countryLabel={COUNTRY_LABELS[Country.MO]} />
            </Flex>

            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}

export default SystemConfigPage
